<script setup lang="ts">
import { ref, onMounted, PropType, computed } from "vue";
import { useI18n } from "vue-i18n";
import { Department, DirectLink, Participant, Project, ProjectRole, Trainer } from "../../types/PersologOnlineTypes.ts";
import { formatDate, getLanguage, isMobileView } from "../../utils/GeneralUtils.ts";
import {
    ensureProductsWithDepartmentsLoaded,
    fetchDefaultRoleNames,
    focusMapping,
    getProductNames,
    productHasBenchmarkDiagram,
    productHasDepartments,
    productHasRoles,
} from "../../utils/ProjectUtils";
import { useProjectDepartmentsStore } from "../../stores/projectDepartments";


const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        default: null,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    departments: {
        type: Array as PropType<Department[]>,
        default: () => [],
    },
    withLabel: {
        type: Boolean,
        default: false,
    },
    roles: {
        type: Array as PropType<ProjectRole[]>,
        required: false,
        default: () => [],
    },
    showRoles: {
        type: Boolean,
        default: false,
    },
    showDirectLinkInfo: {
        type: Boolean,
        default: true,
    },
});

const departmentsStore = useProjectDepartmentsStore();
const productNames = ref();

// Single source of truth: prefer props over fetched data
const departments = computed(() => {
    if (props.departments && props.departments.length > 0) {
        return props.departments;
    }
    return departmentsStore.getForProject(props.project.id ?? 0);
});

const hasDepartments = computed(() => {
    return productHasDepartments(props.project.product) && departments.value.length > 0;
});

const hasRoles = ref(false);
const defaultRoleNames = ref<Record<string, string>>({});

const creationTime = computed(() => {
    if (!props.project?.creation) {
        return "";
    }
    return formatDate(props.project.creation, t("formats.datetime"));
});

const editedTime = computed(() => {
    const editedTime = props.project.editedDate || props.project.creation;
    if (!editedTime) {
        return "";
    }
    return formatDate(editedTime, t("formats.datetime"));
});

const deletionTime = computed(() => {
    if (props.project?.deletionDate === undefined || props.project?.deletionDate === null) {
        return "";
    } else {
        return formatDate(props.project?.deletionDate as string, t("formats.datetime"));
    }
});

const directLinkExpiryTime = computed(() =>
    props.directLink?.expiresAt ? formatDate(props.directLink.expiresAt, t("formats.datetime")) : "",
);

const recipientReleaseTime = computed(() => {
    if (!props.project?.recipientReleaseDate) {
        return "";
    }
    return formatDate(props.project.recipientReleaseDate as string, t("formats.datetime"));
});

/**
 * Determines the focus key based on the selected product
 */
const focusKey = computed(() => {
    if (!props.project.product) return "focus";
    return focusMapping[props.project.product] || focusMapping.default;
});

/**
 * Translated focus title
 */
const focusText = computed(() => {
    return t(`project-configuration.projectFocus.${focusKey.value}.title`);
});

const resolvedRoles = computed(() => {
    return props.roles.map((role) => {
        const backendName = role.name?.trim() ?? "";
        if (backendName !== "") {
            return role;
        }

        const fallback =
            defaultRoleNames.value[role.role] ??
            defaultRoleNames.value[role.role.toLowerCase()] ??
            defaultRoleNames.value[role.role.toUpperCase()] ??
            role.role;

        return {
            ...role,
            name: fallback,
        };
    });
});

onMounted(async () => {
    await ensureProductsWithDepartmentsLoaded();
    await departmentsStore.ensureLoaded(props.project);
    productNames.value = await getProductNames();
    hasRoles.value = props.project && (await productHasRoles(props.project.product));
    defaultRoleNames.value = await fetchDefaultRoleNames(props.project);
});

const checkDirectLinkDepleted = () => {
    return props.directLink ? props.participants.length / props.directLink.quantity >= 1 : false;
};

const getDefaultProductName = () => {
    if (productNames.value && props.project && props.project.defaultProduct) {
        return productNames.value[props.project.defaultProduct] ?? props.project.defaultProduct;
    } else {
        return props.project.defaultProduct;
    }
};
</script>

<template>
    <div v-if="isMobileView()">
        <i class="pi pi-file mr-2"></i><b>{{ project.name }}</b>
    </div>

    <!-- Project Creation time -->
    <div :id="`project-information_div_creation-${project.id}`">
        <i
            class="pi pi-calendar mr-2"
            v-tooltip.left="{ value: t('project-details.creationDate.tooltip'), showDelay: 500 }"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.creationDate.label") }}</strong>
        {{ creationTime }}
    </div>

    <!-- Last Updated -->
    <div :id="`project-information_div_update-time-${project.id}`">
        <i
            class="pi pi-calendar-clock mr-2"
            v-tooltip.left="{ value: t('project-details.lastUpdated.tooltip'), showDelay: 500 }"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.lastUpdated.label") }}</strong>
        {{ editedTime }}
    </div>
    <div
        v-if="project.deletionDate !== undefined && project.deletionDate !== null"
        :id="`project-information_div_deletion-${project.id}`"
    >
        <i
            class="pi pi-calendar-times mr-2"
            v-tooltip.left="{ value: t('project-details.deletionDate.tooltip'), showDelay: 500 }"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.deletionDate.label") }}</strong>
        {{ deletionTime }}
    </div>

    <!-- Focus -->
    <div v-if="project?.focus !== ''" :id="`project-information_div_focus-${project.id}`">
        <i class="pi pi-search mr-2" v-tooltip.left="{ value: t('project-details.focus.tooltip'), showDelay: 500 }"></i>
        <strong v-if="withLabel">{{ focusText }}:</strong>
        {{ project?.focus }}
    </div>

    <!-- Project Language -->
    <div>
        <i
            v-tooltip.left="{ value: t('project-details.language.tooltip'), showDelay: 500 }"
            class="pi pi-globe mr-2"
        ></i>
        <strong v-if="withLabel"> {{ t("project-details.language.label-default") }} </strong>
        {{ getLanguage(project?.language) }}
    </div>

    <!-- Recipients -->
    <div :id="`project-information_div_recipients-${project.id}`">
        <i
            v-tooltip.left="{ value: t('project-details.recipients.label'), showDelay: 500 }"
            class="pi pi-envelope mr-2"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.recipients.label") }}</strong>
        {{
            project.recipients === "TRAINER" && trainer
                ? trainer.firstname + " " + trainer.lastname
                : t("project-configuration.recipients." + project?.recipients?.toLowerCase())
        }}
        <span v-if="project?.additionalRecipient && project?.additionalRecipient?.email !== ''">
            & {{ project?.additionalRecipient?.firstName }} {{ project?.additionalRecipient?.lastName }} ({{
                project?.additionalRecipient?.email
            }})
        </span>
    </div>

    <!-- Recipient release date -->
    <div v-if="project?.recipientReleaseDate" :id="`project-information_div_recipient-release-date-${project.id}`">
        <i
            v-tooltip.left="{ value: t('project-details.recipientReleaseDate.tooltip'), showDelay: 500 }"
            class="pi pi-clock mr-2"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.recipientReleaseDate.label") }}</strong>
        {{ recipientReleaseTime }}
    </div>

    <!-- Project Default Product -->
    <div v-if="project && project.product !== project.defaultProduct">
        <i
            v-tooltip.left="{ value: t('project-details.defaultProduct.label-default'), showDelay: 500 }"
            class="pi pi-cog mr-2"
        ></i>
        <strong v-if="withLabel"> {{ t("project-details.defaultProduct.label-default") }} </strong>
        {{ getDefaultProductName() }}
    </div>

    <!-- Departments -->
    <div v-if="hasDepartments" :id="`project-information_div_departments-${project.id}`">
        <i class="pi pi-building mr-2"></i>
        <strong v-if="withLabel">{{ t("project-details.departments.heading") + ": " }}</strong>
        <span v-for="(department, key) in departments" :key="key">
            {{ department.name }}{{ key < departments.length - 1 ? ", " : null }}
        </span>
    </div>

    <!-- Roles -->
    <div v-if="hasRoles && showRoles" :id="`project-information_div_roles-${project.id}`">
        <i class="pi pi-users mr-2"></i>
        <strong v-if="withLabel">{{ t("project-configuration.roles.label") + ": " }}</strong>
        <span v-for="(role, key) in resolvedRoles" :key="role.role">
            {{ role.name }}{{ key < resolvedRoles.length - 1 ? ", " : null }}
        </span>
    </div>

    <!-- Direct Link -->
    <div v-if="directLink && showDirectLinkInfo" :id="`project-information_div_direct-link-information-${project.id}`">
        <i
            v-tooltip.left="{ value: t('project-configuration.directLink.usedRatio', { used: participants.length, total: directLink?.quantity }), showDelay: 500 }"
            class="pi pi-link mr-2"
            :id="`project-information_i_direct-link-quantity-icon-${project.id}`"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.direct-link.heading") }}:</strong>
        {{ participants.length + "/" + directLink?.quantity }}
        <i
            :id="`project-information_i_direct-link-depleted-icon-${project.id}`"
            v-if="checkDirectLinkDepleted()"
            class="pi pi-exclamation-triangle mr-2"
            v-tooltip="t('project-details.direct-link.depleted')"
        ></i>
    </div>

    <!-- Direct link expiry -->
    <div
        v-if="directLink && showDirectLinkInfo && directLinkExpiryTime"
        :id="`project-information_div_direct-link-expiry-${project.id}`"
    >
        <i
            v-tooltip.left="{ value: t('project-configuration.directLink.expiryTooltip'), showDelay: 500 }"
            class="pi pi-clock mr-2"
        ></i>
        <strong v-if="withLabel">{{ t("project-details.direct-link.expiry") }}</strong>
        {{ directLinkExpiryTime }}
    </div>

    <div
        v-if="!directLink && !productHasBenchmarkDiagram"
        :id="`project-information_div_participant-count-${project.id}`"
    >
        <i class="pi pi-users mr-2" v-tooltip.left="t('project-overview.tooltips.amount')"></i>{{ participants.length }}
    </div>

    <div v-if="project.anonymous"><i class="pi pi-shield mr-2"></i>{{ t("project-details.anonymous.label") }}</div>
</template>
